import { useState, useEffect } from "react";
import type { IAtributo } from "interfaces/loja/atributosProduto";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

const TIPOS_PARA_COMBINACAO: IAtributo["tipo"][] = ["LISTA", "COR"];

export interface ModalGerarVariacoesProps {
  open: boolean;
  onClose: () => void;
  atributosDisponiveis: IAtributo[];
  onConfirmar: (atributosSelecionados: IAtributo[]) => void;
}

export default function ModalGerarVariacoes({
  open,
  onClose,
  atributosDisponiveis,
  onConfirmar,
}: ModalGerarVariacoesProps) {
  const atributosParaCombinacao = atributosDisponiveis.filter(
    (a) =>
      TIPOS_PARA_COMBINACAO.includes(a.tipo) &&
      Array.isArray(a.opcoes) &&
      a.opcoes.length > 0
  );

  const [selecionados, setSelecionados] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (open) {
      setSelecionados(new Set());
    }
  }, [open]);

  const toggleAtributo = (id: number) => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const handleGerar = () => {
    const lista = atributosParaCombinacao.filter((a) => selecionados.has(a.id));
    if (lista.length === 0) {
      return;
    }
    onConfirmar(lista);
    onClose();
  };

  const quantidadeCombinacoes = (() => {
    const selecionadosLista = atributosParaCombinacao.filter((a) =>
      selecionados.has(a.id)
    );
    if (selecionadosLista.length === 0) return 0;
    return selecionadosLista.reduce(
      (acc, a) => acc * (a.opcoes?.length ?? 1),
      1
    );
  })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <span>Gerar variações automaticamente</span>
          <IconButton size="small" onClick={onClose} aria-label="Fechar">
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecione os atributos que deseja usar para gerar as combinações. Serão
          criadas variações para todas as combinações possíveis entre as opções
          dos atributos escolhidos.
        </MDTypography>
        {atributosParaCombinacao.length === 0 ? (
          <MDTypography variant="body2" color="text.secondary">
            Nenhum atributo do tipo LISTA ou COR com opções cadastradas. Cadastre
            em &quot;Gerenciar Atributos&quot;.
          </MDTypography>
        ) : (
          <FormGroup>
            {atributosParaCombinacao.map((atr) => (
              <FormControlLabel
                key={atr.id}
                control={
                  <Checkbox
                    checked={selecionados.has(atr.id)}
                    onChange={() => toggleAtributo(atr.id)}
                    color="primary"
                  />
                }
                label={
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <span>{atr.nome}</span>
                    <MDTypography variant="caption" color="text.secondary">
                      ({atr.tipo} · {atr.opcoes?.length ?? 0} opções)
                    </MDTypography>
                  </MDBox>
                }
              />
            ))}
          </FormGroup>
        )}
        {selecionados.size > 0 && quantidadeCombinacoes > 0 && (
          <MDTypography variant="caption" display="block" sx={{ mt: 2 }}>
            Serão geradas <strong>{quantidadeCombinacoes}</strong> variação(ões).
          </MDTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="outlined" color="dark" onClick={onClose}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleGerar}
          disabled={selecionados.size === 0}
        >
          Gerar variações
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
